import React, { useState, useEffect, useMemo } from 'react';
import { Search, Download, Filter, RefreshCw, Users, MapPin, Calendar, ChevronDown, ChevronUp, ArrowUpDown, Bike, Car, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const RiderSummaryReport = ({ adminId }) => {
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [reportData, setReportData] = useState(null);
  
  // Pagination state
  const [page, setPage] = useState(1);
  const [limit] = useState(50);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRiders, setTotalRiders] = useState(0);
  
  // Sorting state
  const [sortConfig, setSortConfig] = useState({ key: 'city', direction: 'asc' });
  
  // Filter states
  const [filters, setFilters] = useState({
    city: '',
    search: '',
    manager_id: '',
    vehicle_type: '',
    joined_from: '',
    joined_to: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  
  // Filter options
  const [cities, setCities] = useState([]);
  const [managers, setManagers] = useState([]);
  const [vehicleTypes, setVehicleTypes] = useState([]);

  useEffect(() => {
    fetchFilterOptions();
  }, [adminId]);

  useEffect(() => {
    fetchReport();
  }, [adminId, page]);

  const fetchFilterOptions = async () => {
    try {
      const response = await axios.get(`${API}/api/reports/rider-summary/filters`, {
        params: { admin_id: adminId }
      });
      // Sort cities alphabetically by name
      const sortedCities = (response.data.cities || []).sort((a, b) => 
        (a.name || '').localeCompare(b.name || '')
      );
      // Sort managers alphabetically by name
      const sortedManagers = (response.data.managers || []).sort((a, b) => 
        (a.name || '').localeCompare(b.name || '')
      );
      setCities(sortedCities);
      setManagers(sortedManagers);
      setVehicleTypes(response.data.vehicle_types || []);
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  };

  const fetchReport = async () => {
    setLoading(true);
    try {
      const params = { 
        admin_id: adminId, 
        format: 'json',
        page: page,
        limit: limit
      };
      
      if (filters.city) params.city = filters.city;
      if (filters.search) params.search = filters.search;
      if (filters.manager_id) params.manager_id = filters.manager_id;
      if (filters.vehicle_type) params.vehicle_type = filters.vehicle_type;
      if (filters.joined_from) params.joined_from = filters.joined_from;
      if (filters.joined_to) params.joined_to = filters.joined_to;
      
      const response = await axios.get(`${API}/api/reports/rider-summary`, { params });
      setReportData(response.data);
      setTotalPages(response.data.total_pages || 1);
      setTotalRiders(response.data.total_riders || 0);
    } catch (error) {
      toast.error('Failed to load report');
      console.error('Error fetching report:', error);
    } finally {
      setLoading(false);
    }
  };

  const downloadExcel = async () => {
    setDownloading(true);
    try {
      const params = { admin_id: adminId, format: 'excel' };
      
      if (filters.city) params.city = filters.city;
      if (filters.search) params.search = filters.search;
      if (filters.manager_id) params.manager_id = filters.manager_id;
      if (filters.vehicle_type) params.vehicle_type = filters.vehicle_type;
      if (filters.joined_from) params.joined_from = filters.joined_from;
      if (filters.joined_to) params.joined_to = filters.joined_to;
      
      const response = await axios.get(`${API}/api/reports/rider-summary`, {
        params,
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Rider_Summary_${new Date().toISOString().slice(0,10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Report downloaded successfully');
    } catch (error) {
      toast.error('Failed to download report');
      console.error('Error downloading report:', error);
    } finally {
      setDownloading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const applyFilters = () => {
    setPage(1);  // Reset to first page when applying filters
    fetchReport();
    setShowFilters(false);
  };

  const clearFilters = () => {
    setFilters({
      city: '',
      search: '',
      manager_id: '',
      vehicle_type: '',
      joined_from: '',
      joined_to: ''
    });
    setPage(1);  // Reset to first page
    setTimeout(() => fetchReport(), 100);
  };

  const goToPage = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  const hasActiveFilters = Object.values(filters).some(v => v);

  // Sorting logic
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const sortedData = useMemo(() => {
    if (!reportData?.data) return [];
    
    const sorted = [...reportData.data].sort((a, b) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];
      
      // Handle null/undefined
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;
      
      // Numeric columns
      const numericKeys = ['completed_rides', 'cancelled_rides', 'earnings', 'commission_to_admin', 'due_to_admin', 'paid_to_admin'];
      if (numericKeys.includes(sortConfig.key)) {
        const diff = (parseFloat(aVal) || 0) - (parseFloat(bVal) || 0);
        return sortConfig.direction === 'asc' ? diff : -diff;
      }
      
      // String comparison
      const comparison = String(aVal).localeCompare(String(bVal));
      return sortConfig.direction === 'asc' ? comparison : -comparison;
    });
    
    return sorted;
  }, [reportData?.data, sortConfig]);

  // Sortable header component
  const SortableHeader = ({ label, sortKey, align = 'left' }) => {
    const isActive = sortConfig.key === sortKey;
    return (
      <th 
        className={`px-4 py-3 text-${align} text-xs font-semibold uppercase tracking-wider cursor-pointer hover:bg-gray-800 transition-colors select-none`}
        onClick={() => handleSort(sortKey)}
      >
        <div className={`flex items-center gap-1 ${align === 'right' ? 'justify-end' : align === 'center' ? 'justify-center' : ''}`}>
          {label}
          {isActive ? (
            sortConfig.direction === 'asc' ? (
              <ChevronUp className="h-4 w-4 text-yellow-400" />
            ) : (
              <ChevronDown className="h-4 w-4 text-yellow-400" />
            )
          ) : (
            <ArrowUpDown className="h-3 w-3 opacity-40" />
          )}
        </div>
      </th>
    );
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Users className="h-7 w-7 text-blue-600" />
            Rider Summary Report
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Complete overview of all riders with their performance metrics
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
              hasActiveFilters ? 'bg-blue-50 border-blue-300 text-blue-700' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            <Filter className="h-4 w-4" />
            Filters
            {hasActiveFilters && (
              <span className="bg-blue-600 text-white text-xs px-1.5 py-0.5 rounded-full">
                {Object.values(filters).filter(v => v).length}
              </span>
            )}
            <ChevronDown className={`h-4 w-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
          </button>
          
          <button
            onClick={fetchReport}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          
          <button
            onClick={downloadExcel}
            disabled={downloading || !reportData?.data?.length}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
          >
            <Download className={`h-4 w-4 ${downloading ? 'animate-bounce' : ''}`} />
            {downloading ? 'Downloading...' : 'Download Excel'}
          </button>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="bg-white border rounded-xl p-6 shadow-sm">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Search */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Search Rider
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Name or phone..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
            
            {/* City */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <MapPin className="inline h-4 w-4 mr-1" />
                City
              </label>
              <select
                value={filters.city}
                onChange={(e) => handleFilterChange('city', e.target.value)}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All Cities</option>
                {cities.map(city => (
                  <option key={city.id} value={city.name}>{city.name}</option>
                ))}
              </select>
            </div>
            
            {/* Manager */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Users className="inline h-4 w-4 mr-1" />
                Manager
              </label>
              <select
                value={filters.manager_id}
                onChange={(e) => handleFilterChange('manager_id', e.target.value)}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All Managers</option>
                {managers.map(mgr => (
                  <option key={mgr.id} value={mgr.id}>{mgr.name}</option>
                ))}
              </select>
            </div>
            
            {/* Vehicle Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Bike className="inline h-4 w-4 mr-1" />
                Vehicle Type
              </label>
              <select
                value={filters.vehicle_type}
                onChange={(e) => handleFilterChange('vehicle_type', e.target.value)}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All Vehicles</option>
                {vehicleTypes.map(vt => (
                  <option key={vt.id} value={vt.id}>{vt.name}</option>
                ))}
              </select>
            </div>
            
            {/* Joined From */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Calendar className="inline h-4 w-4 mr-1" />
                Joined From
              </label>
              <input
                type="date"
                value={filters.joined_from}
                onChange={(e) => handleFilterChange('joined_from', e.target.value)}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            
            {/* Joined To */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Calendar className="inline h-4 w-4 mr-1" />
                Joined To
              </label>
              <input
                type="date"
                value={filters.joined_to}
                onChange={(e) => handleFilterChange('joined_to', e.target.value)}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          
          <div className="flex justify-end gap-3 mt-4 pt-4 border-t">
            <button
              onClick={clearFilters}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
            >
              Clear All
            </button>
            <button
              onClick={applyFilters}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Apply Filters
            </button>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      {reportData?.totals && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
          <div className="bg-white border rounded-xl p-4">
            <p className="text-xs text-gray-500 uppercase tracking-wide">Total Riders</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{reportData.totals.total_riders}</p>
          </div>
          <div className="bg-white border rounded-xl p-4">
            <p className="text-xs text-gray-500 uppercase tracking-wide">Completed</p>
            <p className="text-2xl font-bold text-green-600 mt-1">{reportData.totals.completed_rides}</p>
          </div>
          <div className="bg-white border rounded-xl p-4">
            <p className="text-xs text-gray-500 uppercase tracking-wide">Cancelled</p>
            <p className="text-2xl font-bold text-red-600 mt-1">{reportData.totals.cancelled_rides}</p>
          </div>
          <div className="bg-white border rounded-xl p-4">
            <p className="text-xs text-gray-500 uppercase tracking-wide">Earnings</p>
            <p className="text-2xl font-bold text-blue-600 mt-1">₹{reportData.totals.earnings?.toLocaleString()}</p>
          </div>
          <div className="bg-white border rounded-xl p-4">
            <p className="text-xs text-gray-500 uppercase tracking-wide">Commission</p>
            <p className="text-2xl font-bold text-purple-600 mt-1">₹{reportData.totals.commission_to_admin?.toLocaleString()}</p>
          </div>
          <div className="bg-white border rounded-xl p-4">
            <p className="text-xs text-gray-500 uppercase tracking-wide">Due</p>
            <p className="text-2xl font-bold text-orange-600 mt-1">₹{reportData.totals.due_to_admin?.toLocaleString()}</p>
          </div>
          <div className="bg-white border rounded-xl p-4">
            <p className="text-xs text-gray-500 uppercase tracking-wide">Paid</p>
            <p className="text-2xl font-bold text-emerald-600 mt-1">₹{reportData.totals.paid_to_admin?.toLocaleString()}</p>
          </div>
        </div>
      )}

      {/* Sort indicator */}
      {sortedData.length > 0 && (
        <div className="flex items-center justify-between text-sm text-gray-500">
          <span>
            Showing {sortedData.length} rider{sortedData.length !== 1 ? 's' : ''} 
            {hasActiveFilters && ' (filtered)'}
          </span>
          <span className="flex items-center gap-1">
            Sorted by <strong className="text-gray-700">{sortConfig.key.replace('_', ' ')}</strong>
            {sortConfig.direction === 'asc' ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
            <button 
              onClick={() => setSortConfig({ key: 'city', direction: 'asc' })}
              className="ml-2 text-blue-600 hover:text-blue-800"
            >
              Reset
            </button>
          </span>
        </div>
      )}

      {/* Data Table */}
      <div className="bg-white border rounded-xl overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-3 text-gray-600">Loading report...</span>
          </div>
        ) : sortedData.length > 0 ? (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-900 text-white">
                  <tr>
                    <SortableHeader label="City" sortKey="city" />
                    <SortableHeader label="Rider Name" sortKey="rider_name" />
                    <SortableHeader label="Phone" sortKey="phone" />
                    <SortableHeader label="Vehicle" sortKey="vehicle_type" align="center" />
                    <SortableHeader label="Status" sortKey="online_status" align="center" />
                    <SortableHeader label="Completed" sortKey="completed_rides" align="center" />
                    <SortableHeader label="Cancelled" sortKey="cancelled_rides" align="center" />
                    <SortableHeader label="Earnings" sortKey="earnings" align="right" />
                    <SortableHeader label="Commission" sortKey="commission_to_admin" align="right" />
                    <SortableHeader label="Due" sortKey="due_to_admin" align="right" />
                    <SortableHeader label="Paid" sortKey="paid_to_admin" align="right" />
                    <SortableHeader label="Joined" sortKey="joined_date" align="center" />
                    <SortableHeader label="Manager" sortKey="manager" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                {sortedData.map((rider, index) => (
                  <tr key={index} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 text-sm text-gray-900">{rider.city}</td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{rider.rider_name}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{rider.phone}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                        rider.vehicle_type === 'Bike' ? 'bg-orange-100 text-orange-700' :
                        rider.vehicle_type === 'Auto' ? 'bg-green-100 text-green-700' :
                        rider.vehicle_type === 'Car' ? 'bg-blue-100 text-blue-700' :
                        'bg-gray-100 text-gray-600'
                      }`}>
                        {rider.vehicle_type}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        rider.online_status === 'Online' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${
                          rider.online_status === 'Online' ? 'bg-green-500' : 'bg-gray-400'
                        }`}></span>
                        {rider.online_status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center text-sm font-medium text-green-600">{rider.completed_rides}</td>
                    <td className="px-4 py-3 text-center text-sm font-medium text-red-600">{rider.cancelled_rides}</td>
                    <td className="px-4 py-3 text-right text-sm font-medium text-gray-900">₹{rider.earnings?.toLocaleString()}</td>
                    <td className="px-4 py-3 text-right text-sm text-purple-600">₹{rider.commission_to_admin?.toLocaleString()}</td>
                    <td className="px-4 py-3 text-right text-sm text-orange-600">₹{rider.due_to_admin?.toLocaleString()}</td>
                    <td className="px-4 py-3 text-right text-sm text-emerald-600">₹{rider.paid_to_admin?.toLocaleString()}</td>
                    <td className="px-4 py-3 text-center text-sm text-gray-600">{rider.joined_date}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{rider.manager}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
              <div className="text-sm text-gray-600">
                Showing {((page - 1) * limit) + 1} - {Math.min(page * limit, totalRiders)} of {totalRiders} riders
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => goToPage(page - 1)}
                  disabled={page === 1}
                  className="px-3 py-1.5 text-sm border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                
                {/* Page numbers */}
                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (page <= 3) {
                      pageNum = i + 1;
                    } else if (page >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = page - 2 + i;
                    }
                    return (
                      <button
                        key={pageNum}
                        onClick={() => goToPage(pageNum)}
                        className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                          page === pageNum
                            ? 'bg-blue-600 text-white'
                            : 'border hover:bg-gray-100'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                
                <button
                  onClick={() => goToPage(page + 1)}
                  disabled={page === totalPages}
                  className="px-3 py-1.5 text-sm border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
        </>
        ) : (
          <div className="flex flex-col items-center justify-center py-20 text-gray-500">
            <Users className="h-12 w-12 mb-4 opacity-50" />
            <p className="text-lg font-medium">No riders found</p>
            <p className="text-sm">Try adjusting your filters</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default RiderSummaryReport;
